// src/components/MobileKeyboard.jsx
import React, { useRef, useState, useEffect, useMemo } from "react";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import { layouts } from "./layouts";
import { useLanguage } from "../contexts/LanguageContext";
import questionsConfig from "../config/questions.json";
import "../assets/css/mobileKeyboard.css";

// Devanagari matras (vowel signs) - Unicode ranges
// Includes: vowel signs (\u093E-\u094C), vocalic r (\u093C)
const DEVANAGARI_MATRAS = /[\u093C\u093E-\u094C\u0955\u0956\u0957]/; // Devanagari vowel signs (matras)
const DEVANAGARI_DIACRITICS = /[\u0902\u0903]/; // Anusvara (\u0902) and Visarga (\u0903) - can combine with matras
const DEVANAGARI_CONSONANTS = /[\u0915-\u0939]/; // Devanagari consonants
const DEVANAGARI_VOWELS = /[\u0905-\u0914]/; // Devanagari independent vowels

/**
 * Finds the last consonant and its matra/diacritics (if any) in the result string
 * Returns the index of the consonant, whether it has a matra, and the end index of diacritics
 */
const findLastConsonantInfo = (str) => {
  for (let i = str.length - 1; i >= 0; i--) {
    const char = str[i];
    if (DEVANAGARI_CONSONANTS.test(char)) {
      // Check if there's a matra or diacritics after this consonant
      let j = i + 1;
      let hasMatra = false;
      let diacriticsEnd = i + 1;
      
      // Check for matra (only one matra allowed)
      if (j < str.length && DEVANAGARI_MATRAS.test(str[j])) {
        hasMatra = true;
        j++;
        diacriticsEnd = j;
      }
      
      // Check for diacritics (anusvara/visarga) - can come after matra
      while (j < str.length && DEVANAGARI_DIACRITICS.test(str[j])) {
        j++;
        diacriticsEnd = j;
      }
      
      return { index: i, hasMatra, diacriticsEnd };
    }
  }
  return null;
};

/**
 * Processes Devanagari text to ensure matras are properly combined with consonants
 * Allows replacing matras on the last consonant by typing a new matra
 */
const processDevanagariInput = (text) => {
  if (!text) return text;
  
  // Check if text contains Devanagari characters
  const hasDevanagari = /[\u0900-\u097F]/.test(text);
  if (!hasDevanagari) return text;
  
  // Rebuild the string by grouping consonants with their matras
  let result = '';
  let i = 0;
  
  while (i < text.length) {
    const char = text[i];
    
    // If it's a Devanagari consonant
    if (DEVANAGARI_CONSONANTS.test(char)) {
      result += char;
      i++;
      
      // Collect matra (only one allowed) and diacritics (can be multiple)
      let matra = null;
      const diacritics = [];
      
      // First, collect matra (only one)
      if (i < text.length && DEVANAGARI_MATRAS.test(text[i])) {
        matra = text[i];
        i++;
      }
      
      // Then, collect diacritics (anusvara/visarga) - can be multiple
      while (i < text.length && DEVANAGARI_DIACRITICS.test(text[i])) {
        diacritics.push(text[i]);
        i++;
      }
      
      // Add matra if found
      if (matra) {
        result += matra;
      }
      
      // Add diacritics
      if (diacritics.length > 0) {
        result += diacritics.join('');
      }
    }
    // If it's an independent vowel, add it as-is
    else if (DEVANAGARI_VOWELS.test(char)) {
      result += char;
      i++;
    }
    // If it's a matra
    else if (DEVANAGARI_MATRAS.test(char)) {
      // Find the last consonant in the result
      const lastConsonantInfo = findLastConsonantInfo(result);
      
      if (lastConsonantInfo) {
        const { index, hasMatra } = lastConsonantInfo;
        
        if (hasMatra) {
          // The last consonant already has a matra, replace it with the new one
          result = result.slice(0, index + 1) + char + result.slice(index + 2);
        } else {
          // The last consonant doesn't have a matra, add this one
          result += char;
        }
      } else {
        // No consonant found, skip standalone matra
      }
      i++;
    }
    // If it's a diacritic (anusvara/visarga)
    else if (DEVANAGARI_DIACRITICS.test(char)) {
      // Find the last consonant in the result
      const lastConsonantInfo = findLastConsonantInfo(result);
      
      if (lastConsonantInfo) {
        // Add the diacritic after the consonant (and its matra if any)
        const { diacriticsEnd } = lastConsonantInfo;
        result = result.slice(0, diacriticsEnd) + char + result.slice(diacriticsEnd);
      } else {
        // No consonant found, skip standalone diacritic
      }
      i++;
    }
    // For all other characters (spaces, punctuation, etc.), add them as-is
    else {
      result += char;
      i++;
    }
  }
  
  return result;
};

export default function MobileKeyboard({ onChange, inputName = "default", inputValue = "", onReturn, isLastQuestion = false, isSubmitting = false, isRequired = false, forceEnglishKeyboard = false }) {
  const keyboardRef = useRef(null);
  const { language, translations } = useLanguage();
  
  // Determine initial layout based on language
  const getInitialLayoutName = () => {
    const isDevanagari = !forceEnglishKeyboard && (language === "hi" || language === "mr");
    return isDevanagari ? "page1" : "default";
  };
  
  const [layoutName, setLayoutName] = useState(getInitialLayoutName);
  const previousInputRef = useRef("");

  // Calculate validation based on current input value
  const isCurrentQuestionValid = useMemo(() => {
    if (!isRequired) {
      return true;
    }
    // For text/textarea inputs, check if value exists and is not empty
    return inputValue && inputValue.trim() !== '';
  }, [isRequired, inputValue]);

  // Calculate disabled state
  const isSendDisabled = isSubmitting || !isCurrentQuestionValid;

  // Calculate button theme based on current disabled state - always in sync
  const buttonTheme = useMemo(() => {
    return isSendDisabled
      ? [
          {
            class: "hg-button-disabled",
            buttons: "{send}"
          }
        ]
      : [];
  }, [isSendDisabled]);

  // Map language codes from LanguageContext to layout keys
  const getLayoutKey = (lang) => {
    const languageMap = {
      en: "english",
      mr: "marathi",
      hi: "hindi"
    };
    return languageMap[lang] || "english";
  };

  const languageLayoutKey = getLayoutKey(language);
  const isDevanagariLanguage = !forceEnglishKeyboard && (language === "hi" || language === "mr");

  // Create hybrid layout when forcing English keyboard: English letters but language-specific numbers
  const currentLayout = useMemo(() => {
    if (forceEnglishKeyboard) {
      return {
        default: layouts.english.default,
        shift: layouts.english.shift,
        numbers: layouts.english.numbers
      };
    }
    const layout = layouts[languageLayoutKey];
    // Ensure layout exists and has all required properties
    if (!layout) {
      return layouts.english;
    }
    return layout;
  }, [forceEnglishKeyboard, languageLayoutKey]);

  // Reset layout when language changes or forceEnglishKeyboard changes
  // Initialize layout to page1 for Devanagari languages, default for others
  useEffect(() => {
    if (isDevanagariLanguage && !forceEnglishKeyboard) {
      setLayoutName("page1");
    } else {
      setLayoutName("default");
    }
  }, [language, forceEnglishKeyboard, isDevanagariLanguage]);

  // Sync keyboard with input value
  useEffect(() => {
    if (keyboardRef.current) {
      keyboardRef.current.setInput(inputValue, inputName);
      // Update previous input ref to stay in sync
      previousInputRef.current = inputValue;
    }
  }, [inputValue, inputName]);

  const handleKeyPress = (button) => {
    if (button === "{shift}") {
      setLayoutName(layoutName === "default" ? "shift" : "default");
      return;
    }
    if (button === "{numbers}") {
      setLayoutName("numbers");
      return;
    }
    if (button === "{letters}") {
      // Return to page1 for Devanagari, default for others
      if (isDevanagariLanguage && !forceEnglishKeyboard) {
        setLayoutName("page1");
      } else {
        setLayoutName("default");
      }
      return;
    }
    // Handle page indicator buttons (show current page but navigate to other page)
    if (button === "{page1indicator}") {
      setLayoutName("page2"); // On page1, clicking indicator goes to page2
      return;
    }
    if (button === "{page2indicator}") {
      setLayoutName("page1"); // On page2, clicking indicator goes to page1
      return;
    }
    if (button === "{send}") {
      if (onReturn && !isSendDisabled) {
        onReturn();
      }
      return;
    }
  };

  const handleChange = (input) => {
    // Process Devanagari input to ensure proper matra combination
    let processedInput = input;
    
    if (isDevanagariLanguage) {
      // Check if a matra was just added to a consonant that already has one
      const previousInput = previousInputRef.current;
      
      // If input is longer and ends with a matra or diacritic, check if we need special handling
      if (previousInput && input.length > previousInput.length) {
        const lastChar = input[input.length - 1];
        if (DEVANAGARI_MATRAS.test(lastChar)) {
          // A matra was just added - check if the previous input ended with a consonant+matra
          const prevLastConsonantInfo = findLastConsonantInfo(previousInput);
          
          if (prevLastConsonantInfo && prevLastConsonantInfo.hasMatra) {
            // The last consonant already had a matra, replace it with the new one
            const { index } = prevLastConsonantInfo;
            processedInput = previousInput.slice(0, index + 1) + lastChar + previousInput.slice(index + 2);
            
            // Update the keyboard to reflect the corrected input
            if (keyboardRef.current) {
              keyboardRef.current.setInput(processedInput, inputName);
            }
          } else {
            // Process normally
            processedInput = processDevanagariInput(input);
          }
        } else if (DEVANAGARI_DIACRITICS.test(lastChar)) {
          // A diacritic was just added - process normally (diacritics can combine with matras)
          processedInput = processDevanagariInput(input);
        } else {
          // Process normally
          processedInput = processDevanagariInput(input);
        }
      } else {
        // Process normally
        processedInput = processDevanagariInput(input);
      }
      
      // Update previous input ref
      previousInputRef.current = processedInput;
    }
    
    onChange(processedInput);
  };


  // Get English translations for letters button when forcing English keyboard
  const englishTranslations = questionsConfig.translations.en;
  const lettersButtonText = forceEnglishKeyboard 
    ? englishTranslations.keyboard.letters 
    : translations.keyboard.letters;
  
  // Shift button text: show "abc" in lowercase mode, "ABC" in uppercase mode for English keyboard
  const shiftButtonText = useMemo(() => {
    // For English keyboard (either forced or when language is English)
    if (forceEnglishKeyboard || language === "en") {
      return layoutName === "shift" ? "abc" : "ABC";
    }
    return translations.keyboard.shift;
  }, [forceEnglishKeyboard, language, layoutName, translations.keyboard.shift]);

  // Ensure layoutName exists in currentLayout, fallback to default if not
  const safeLayoutName = useMemo(() => {
    if (currentLayout && currentLayout[layoutName]) {
      return layoutName;
    }
    // Fallback to a layout that exists
    if (currentLayout && currentLayout.default) {
      return "default";
    }
    return layoutName;
  }, [layoutName, currentLayout]);

  // Ensure currentLayout is valid before rendering
  if (!currentLayout || typeof currentLayout !== 'object') {
    return null;
  }

  return (
    <div className="mkb-wrapper">
      <Keyboard
        keyboardRef={(r) => (keyboardRef.current = r)}
        layoutName={safeLayoutName}
        layout={currentLayout}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        inputName={inputName}
        display={{
          "{shift}": shiftButtonText,
          "{numbers}": translations.keyboard.numbers,
          "{letters}": lettersButtonText,
          "{backspace}": "⌫",
          "{bksp}": "⌫",
          "{space}": translations.keyboard.space,
          "{send}": isLastQuestion ? translations.buttons.submit : translations.keyboard.next,
          "{page1indicator}": "1/2",
          "{page2indicator}": "2/2"
        }}
        buttonTheme={buttonTheme}
        theme="simple-keyboard hg-theme-default"
      />
    </div>
  );
}
